/**
 * 監督者自己填的名字。前後端共用同一份規則。
 *
 * 它回答的是「戳我的是誰」，不是「這個人是誰」——沒有驗證，冒名在朋友圈裡
 * 不是安全問題。這裡管的有三件事：**看得懂**（只收中英數）、**塞得下**
 * （顯示寬度上限），以及**同一天內不能換**。
 *
 * 第三件事看起來不像暱稱的責任，但它正是前兩件事的代價：暱稱同時被當成
 * 顯示標籤與身分用（`cheers` 的主鍵是 `(日期, 暱稱)`），而標籤隨時可以改，
 * 於是改個名字就等於換一個身分、又拿到一次戳的機會。補那個落差的規則跟著
 * 暱稱走，不跟著戳走。
 */

/**
 * 上限以**顯示寬度**計而不是字數。
 *
 * 中文字約是英文字母的兩倍寬，`小明` 與 `Alexander` 字數相近但寬度差一倍；
 * 用字數當上限的話，肉羹那行台詞與月曆格子一定會被英文暱稱撐破。
 *
 * 8 ＝ 4 個中文字 ＝ 8 個英文字母。
 */
export const NICKNAME_MAX_WIDTH = 8;

/**
 * 只收中英數。
 *
 * 中文只涵蓋 BMP 的常用區（一-鿿）——罕用字要用增補平面的代理對表示，
 * 那些會被擋下來。這是刻意的取捨：允許代理對就得處理「半個字」的寬度計算，
 * 而這裡真正要防的是空白、emoji 與標記符號。
 */
const ALLOWED = /^[一-鿿A-Za-z0-9]+$/;

/** 中文字算兩格，其餘算一格。 */
export function displayWidth(value: string): number {
  let width = 0;
  for (const char of value) width += char >= "一" && char <= "鿿" ? 2 : 1;
  return width;
}

/**
 * 收得下就回正規化後的暱稱，收不下回 null。
 *
 * 回 null 而不是丟例外：呼叫端要做的事是「顯示為什麼不行」，不是接例外。
 * 前後端都要呼叫這支——前端擋是為了當場說明，後端擋才是真的擋。
 */
export function normalizeNickname(value: unknown): string | null {
  if (typeof value !== "string") return null;
  // 前後多打的空白是手滑，不是違規；中間的空白才是
  const trimmed = value.trim();
  if (!ALLOWED.test(trimmed)) return null;
  if (displayWidth(trimmed) > NICKNAME_MAX_WIDTH) return null;
  return trimmed;
}

/**
 * 一台裝置今天戳的那一次。缺任一項就當作還沒戳過。
 *
 * 兩項都是可選的：來源是 session cookie 的 payload，而**沒有這兩個欄位的
 * 舊 cookie 仍然算數**（見 `functions/_lib/auth.ts`）。
 */
export interface CheerIdentity {
  nickname?: string | null;
  date?: string | null;
}

/**
 * 今天已經綁定的名字，還沒綁就回 null。
 *
 * **沒有「已鎖定」旗標**——記著的日期不是今天就當沒鎖過，到期是日期比較的
 * 結果而不是一個要維護的狀態。與 `shared/prediction.ts` 同一個做法：狀態
 * 少一個欄位，就少一處會忘記清掉的地方。
 *
 * 回「已綁定的名字」而不是布林：呼叫端要做的是把那個名字寫進錯誤訊息
 * （「今天已經用『阿明』戳過了」），回布林的話它得再去 cookie 撈第二次。
 *
 * 與這個檔案裡的其他函式一樣**不讀系統時間**——`today` 由呼叫端傳入。
 */
export function lockedNicknameFor(identity: CheerIdentity, today: string): string | null {
  if (!identity.nickname || !identity.date) return null;
  return identity.date === today ? identity.nickname : null;
}
